import {
  BarChart3,
  Building2,
  CalendarCheck,
  Share2,
  Plane,
  Shield,
  Users,
} from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import { PageBanner } from "../../components/ui/PageBanner";
import { useRole } from "../../hooks/useRole";
import { api } from "../../services/api";

/**
 * The administration hub.
 *
 * Two things were wrong with it and both were invisible while the API behind
 * it answered 403 to everybody. Half the cards pointed at /admin/statistics
 * and /admin/settings, which are not routes — React Router renders nothing for
 * a path it does not know, so they led to a blank page. And the overview tiles
 * showed "--", which was at least honest, while the endpoint that should fill
 * them returned invented figures.
 *
 * The cards now point only at pages that exist, and the tiles show counted
 * numbers or say they could not be read.
 */

interface AdminStats {
  users: { total: number; active: number; new_this_month: number };
  health_records: { total: number | null };
  travels: { total: number; upcoming: number };
  documents: { total: number };
  system: { uptime: number; version: string };
}

const adminCards = [
  {
    icon: Users,
    title: "User Management",
    description: "Find accounts, review roles, enable and disable access",
    href: "/admin/users",
    color: "bg-blue-50 border-blue-200 hover:bg-blue-100",
  },
  {
    icon: CalendarCheck,
    title: "Demo Bookings",
    description: "Demo requests that have come in, and their state",
    href: "/admin/demo-bookings",
    color: "bg-amber-50 border-amber-200 hover:bg-amber-100",
  },
  {
    icon: Building2,
    title: "Healthcare Providers",
    description: "Clinics and practices listed for travellers",
    href: "/admin/healthcare",
    color: "bg-green-50 border-green-200 hover:bg-green-100",
  },
  {
    icon: Plane,
    title: "Tour Operators",
    description: "Operator partners and their trips",
    href: "/admin/tours",
    color: "bg-sky-50 border-sky-200 hover:bg-sky-100",
  },
  {
    icon: Share2,
    title: "Affiliate Partners",
    description: "Referral partners, their codes and their performance",
    href: "/admin/affiliates",
    color: "bg-purple-50 border-purple-200 hover:bg-purple-100",
  },
];

function Tile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | null | undefined;
  tone: string;
}) {
  const fmt = useLocaleFormat();
  return (
    <div className={`rounded-lg p-4 ${tone}`}>
      <p className="text-sm font-medium opacity-80">{label}</p>
      <p className="text-2xl font-bold">
        {value === null || value === undefined ? "—" : fmt.number(value, 0)}
      </p>
    </div>
  );
}

const AdminPage: React.FC = () => {
  const { isAdmin, user } = useRole();
  const fmt = useLocaleFormat();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin", "stats"],
    queryFn: async () => {
      const response = await api.get("/admin/stats");
      return response.data.data.stats as AdminStats;
    },
    enabled: isAdmin,
    staleTime: 60_000,
  });

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-96 bg-gray-50 rounded-lg">
        <Shield className="h-16 w-16 text-gray-400 mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Access denied</h1>
        <p className="text-gray-600 text-center max-w-md">
          This page is available to administrators only. If you have just been
          granted access, sign out and in again — the role travels in your
          access token, and yours predates the grant.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <PageBanner
        icon={Shield}
        title="Admin"
        description="Accounts, partners and system overview"
        gradient="from-red-500 to-pink-500"
      />

      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Signed in as</p>
              <p className="font-medium text-gray-900">{user?.email}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Permission</p>
              <p className="font-medium text-red-600">Administrator</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {adminCards.map((card) => {
            const IconComponent = card.icon;
            return (
              <Link
                key={card.href}
                to={card.href}
                className={`block p-6 rounded-lg border-2 transition-all duration-200 ${card.color}`}
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-white rounded-lg shadow-sm">
                    <IconComponent className="h-6 w-6 text-gray-700" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {card.title}
                  </h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {card.description}
                </p>
              </Link>
            );
          })}
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Quick Overview
            </h2>
            <span className="text-sm text-gray-500">
              {isLoading
                ? "Loading…"
                : isError
                  ? "Could not be read"
                  : `Version ${data?.system.version ?? "—"}`}
            </span>
          </div>

          {isError ? (
            <p className="text-sm text-gray-600">
              The statistics could not be loaded. Nothing is shown rather than
              something invented.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Tile
                label="Accounts"
                value={data?.users.total}
                tone="bg-blue-50 text-blue-900"
              />
              <Tile
                label="Active (30 days)"
                value={data?.users.active}
                tone="bg-green-50 text-green-900"
              />
              <Tile
                label="Trips"
                value={data?.travels.total}
                tone="bg-sky-50 text-sky-900"
              />
              <Tile
                label="Documents"
                value={data?.documents.total}
                tone="bg-purple-50 text-purple-900"
              />
            </div>
          )}

          {/* A count of clinical resources, never their contents (REQ-015). */}
          {!isError && (
            <p className="mt-4 text-sm text-gray-500 flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              {data?.health_records.total === null
                ? "Health records: count unavailable"
                : `Health records stored: ${
                    data?.health_records.total != null
                      ? fmt.number(data.health_records.total, 0)
                      : "—"
                  }`}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
